import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import PulseLoader from 'react-spinners/PulseLoader';
import { map, omit } from 'lodash';
import Swal from 'sweetalert2/dist/sweetalert2';
import 'sweetalert2/src/sweetalert2.scss';

import { useServices } from '@services';
import { Table } from '@component';

export const UsersTable = ({ onViewRow, columns, version, className, filter, defaultSortBy }) => {
  const { functions, db } = useServices();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState();
  const filteredUsers = useMemo(() => {
    const search = filter.toLowerCase();
    return users?.filter(
      u =>
        (u.firstName || '').toLowerCase().includes(search) ||
        (u.lastName || '').toLowerCase().includes(search) ||
        (u.displayName || '').toLowerCase().includes(search) ||
        (u.email || '').toLowerCase().includes(search) ||
        (u.role || '').toLowerCase().includes(search) ||
        (u.uid || '').toLowerCase().includes(search)
    );
  }, [users, filter]);

  const confirmDeleteUser = uid => {
    return Swal.fire({
      title: 'Are you sure?',
      text: 'Are you sure you want to delete this user?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, keep it',
    }).then(result => {
      if (result.value) {
        return deleteUser(uid);
      }
      return Promise.resolve();
    });
  };

  const deleteUser = uid => {
    if (process.env.REACT_APP_ALLOW_DELETE !== 'true') return Promise.resolve();
    setLoading(true);
    const deleteUserFunction = functions.httpsCallable('api/users/delete');
    return deleteUserFunction(uid)
      .then(() => {
        toast.success(`User Deleted`);
      })
      .catch(err => {
        toast.error(`Unknown error deleting User`);
      })
      .finally(() => {
        getFirestoreUsers();
        deleteUserCollection(db, uid)
        setLoading(false);
      });
  };

  function deleteUserCollection(db, userToDelete) {
    db.collection('users').doc(userToDelete).delete();
  }

  const getFirestoreUsers = useCallback(
    async searchKey => {
      setLoading(true);
      const getFirestoreUsersFunction = functions.httpsCallable('api/users/getFirestoreUsers');
      const res = await getFirestoreUsersFunction(searchKey);

      const mapped = map(res.data.users, user => {
        return {
          firstName: user['firstName'],
          lastName: user['lastName'] || '',
          role: user['role'] || '',
          email: user['email'] || '',
          uid: user['uid'] || '',
          displayName: user['displayName'] || '',
          country: user['country'] || '',
          state: user['state'] || '',
          country_code: user['country_code'] || '',
          stateCode: user['stateCode'] || ''
        };
      });

      mapped.sort((a, b) => (a.firstName || '').localeCompare(b.firstName || ''));

      setLoading(false);
      setUsers(mapped);
    },
    [functions, db]
  );

  useEffect(() => {
    getFirestoreUsers(filter);
  }, [version, filter, getFirestoreUsers]);

  const rowActions = row => (
    <div className="flex justify-center items-center">
      <button onClick={() => onViewRow(row.original)} className="text-primary-500 px-4">
        View
      </button>
      <button onClick={() => confirmDeleteUser(row.original.uid)} className="text-primary-500 px-4">
        Delete
      </button>
    </div>
  );

  if (loading) return <PulseLoader></PulseLoader>;

  return (
    <div className={className}>
      {filteredUsers && (
        <Table columns={columns} data={filteredUsers} actions={rowActions} defaultSortBy={defaultSortBy} />
      )}
    </div>
  );
};
